import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { UserPlus, Mail } from 'lucide-react';
import { accountSignUp, accountConfirmSignUp } from '../accountAuth';
import { Button, Card, Input, Alert } from './ui';

const AccountSignup = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = searchParams.get('next');
  const [step, setStep] = useState<1 | 2>(1);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      await accountSignUp(email, password);
      setStep(2);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Sign up failed';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await accountConfirmSignUp(email, code);
      navigate(next || '/my-codes');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Confirmation failed';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Create Account - YouWont</title>
      </Helmet>
      <div className="min-h-screen py-10 px-4 sm:px-6 lg:px-8 flex items-start justify-center">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-brand/10 text-brand dark:bg-brand/20 dark:text-blue-400 mb-4">
              <UserPlus size={28} />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100 mb-2">
              {step === 1 ? 'Join the Club' : 'Check Your Inbox'}
            </h1>
            <p className="text-slate-500 dark:text-slate-400">
              {step === 1
                ? "It's free. Your dad would approve."
                : `We sent a 6-digit code to ${email}`}
            </p>
          </div>

          {/* Step indicators */}
          <div className="flex items-center justify-center mb-8 gap-4">
            {[1, 2].map((s) => (
              <div key={s} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step >= s
                      ? 'bg-brand text-white'
                      : 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-400'
                  }`}
                >
                  {s}
                </div>
                {s < 2 && (
                  <div
                    className={`w-12 h-0.5 mx-2 ${
                      step > s ? 'bg-brand' : 'bg-slate-200 dark:bg-slate-700'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>

          <Card className="brand-panel animate-fade-in">
            {step === 1 ? (
              <form onSubmit={handleSignUp} className="space-y-4">
                <Input
                  label="Email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <Input
                  label="Password"
                  type="password"
                  placeholder="Min 8 chars, upper, lower, number, symbol"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  hint="Min 8 characters with uppercase, lowercase, number, and symbol"
                  required
                />
                <Input
                  label="Confirm Password"
                  type="password"
                  placeholder="Repeat password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />

                {error && <Alert variant="danger">{error}</Alert>}

                <Button type="submit" variant="primary" disabled={loading} className="w-full">
                  {loading ? 'Creating Account...' : 'Create Account'}
                </Button>

                <p className="text-center text-sm text-slate-500 dark:text-slate-400">
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/account/login')}
                    className="text-brand hover:text-brand-600 underline dark:text-blue-400"
                  >
                    Log in
                  </button>
                </p>
              </form>
            ) : (
              <form onSubmit={handleConfirm} className="space-y-4">
                <div className="flex items-center gap-3 p-3 rounded-xl bg-brand/5 dark:bg-brand/10 border border-brand/15 dark:border-brand/20">
                  <Mail size={18} className="text-brand dark:text-blue-400 flex-shrink-0" />
                  <p className="text-sm text-slate-700 dark:text-slate-300">
                    Check your inbox at <strong>{email}</strong> for a 6-digit verification code.
                  </p>
                </div>

                <Input
                  label="Verification Code"
                  type="text"
                  placeholder="123456"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                />

                {error && <Alert variant="danger">{error}</Alert>}

                <Button type="submit" variant="primary" disabled={loading || code.length < 6} className="w-full">
                  {loading ? 'Verifying...' : 'Verify & Continue'}
                </Button>

                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setStep(1)}
                  className="w-full"
                >
                  Back
                </Button>
              </form>
            )}
          </Card>
        </div>
      </div>
    </>
  );
};

export default AccountSignup;
